import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, Upload, Edit, Search, Loader2, Mail, Phone } from "lucide-react";

const DEFAULT_GRADES = [
  "Πάρεδρος Πρωτοδικών",
  "Πρωτοδίκης",
  "Πρωτοδίκης Γενικής Επετηρίδας",
  "Πρωτοδίκης Ειδικής Επετηρίδας",
  "Πρόεδρος Πρωτοδικών",
  "Πρόεδρος Πρωτοδικών Γενικής Επετηρίδας",
  "Πρόεδρος Πρωτοδικών Ειδικής Επετηρίδας",
  "Εφέτης",
  "Εφέτης Γενικής Επετηρίδας",
  "Εφέτης Ειδικής Επετηρίδας",
  "Προεδρεύων Εφέτης",
  "Πρόεδρος Εφετών",
];

const blankForm = { full_name: "", grade: "Πρωτοδίκης", mobile: "", email: "", active: true, notes: "" };

const PretrialJudgesPanel = ({ courtType }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [grades, setGrades] = useState(DEFAULT_GRADES);

  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => { fetchJudges(); /* eslint-disable-next-line */ }, [courtType, q]);
  useEffect(() => {
    axios.get(`${API}/pretrial-tools/judge-grades`)
      .then(r => Array.isArray(r.data?.grades) && r.data.grades.length && setGrades(r.data.grades))
      .catch(() => {});
  }, []);

  const fetchJudges = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/pretrial-judges`, { params: { court_type: courtType, q: q || undefined } });
      setJudges(r.data?.judges || []);
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης δικαστών");
    } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(blankForm); setDialogOpen(true); };
  const openEdit = (j) => { setEditing(j); setForm({ ...j }); setDialogOpen(true); };

  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Συμπληρώστε ονοματεπώνυμο"); return; }
    try {
      const payload = { ...form, court_type: courtType };
      if (editing?.id) {
        await axios.put(`${API}/pretrial-judges/${editing.id}`, payload);
        toast.success("Ενημερώθηκε");
      } else {
        await axios.post(`${API}/pretrial-judges`, payload);
        toast.success("Προστέθηκε");
      }
      setDialogOpen(false);
      fetchJudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    }
  };

  const removeJudge = async (j) => {
    if (!window.confirm(`Διαγραφή του ${j.full_name};`)) return;
    try {
      await axios.delete(`${API}/pretrial-judges/${j.id}`);
      toast.success("Διαγράφηκε");
      fetchJudges();
    } catch (e) { toast.error("Σφάλμα διαγραφής"); }
  };

  const runBulkImport = async () => {
    if (!bulkText.trim()) { toast.error("Επικολλήστε λίστα δικαστών"); return; }
    setBulkLoading(true);
    try {
      const r = await axios.post(`${API}/pretrial-judges/bulk-import`, {
        court_type: courtType,
        text: bulkText,
        replace_existing: bulkReplace,
      });
      toast.success(r.data?.message || "Εισήχθησαν");
      setBulkText("");
      setBulkOpen(false);
      fetchJudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εισαγωγής");
    } finally { setBulkLoading(false); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Δικαστές Προδικαστικού Ελέγχου — {courtType}
            </CardTitle>
            <CardDescription>
              Στα emails αυτών των δικαστών στέλνονται οι Πράξεις Ορισμού. Σύνολο: <strong>{judges.length}</strong>
            </CardDescription>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="pj-bulk-open">
                    <Upload className="w-4 h-4 mr-1" /> Bulk Import (paste λίστα)
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Bulk Import Δικαστών — {courtType}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      Μία γραμμή ανά δικαστή. Μορφή: <code className="bg-slate-100 px-1">Όνομα Επώνυμο | Βαθμός | Κινητό | Email</code>
                      <br />Διαχωριστικά που υποστηρίζονται: <code>|</code> ή <code>tab</code> ή <code>;</code>
                    </p>
                    <Textarea
                      rows={14}
                      value={bulkText}
                      onChange={e => setBulkText(e.target.value)}
                      placeholder={"Ιωάννης Παπαδόπουλος | Πρόεδρος Πρωτοδικών | 6912345678 | i.papadopoulos@example.gr\nΕλένη Νικολαΐδου | Πρωτοδίκης Ειδικής Επετηρίδας | 6987654321 | e.nikolaidou@example.gr\n..."}
                      className="font-mono text-xs"
                      data-testid="pj-bulk-text"
                    />
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={bulkReplace} onChange={e => setBulkReplace(e.target.checked)} data-testid="pj-bulk-replace" />
                      <span className="text-red-700">Διαγραφή όλων των υπαρχόντων δικαστών {courtType} πριν την εισαγωγή</span>
                    </label>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setBulkOpen(false)}>Άκυρο</Button>
                    <Button onClick={runBulkImport} disabled={bulkLoading} data-testid="pj-bulk-submit">
                      {bulkLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                      Εισαγωγή
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button size="sm" onClick={openCreate} data-testid="pj-add">
                <Plus className="w-4 h-4 mr-1" /> Προσθήκη Δικαστή
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Αναζήτηση: όνομα, email, κινητό..."
            className="max-w-sm"
            data-testid="pj-search"
          />
        </div>
        {loading ? (
          <div className="text-center py-6 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : judges.length === 0 ? (
          <p className="text-center py-6 text-slate-400 text-sm italic">
            Δεν έχουν προστεθεί δικαστές. Χρησιμοποιήστε «Bulk Import» για γρήγορη εισαγωγή ~{courtType === "Μονομελές" ? 150 : 100} ονομάτων.
          </p>
        ) : (
          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Ονοματεπώνυμο</th>
                  <th className="p-2 text-left">Βαθμός</th>
                  <th className="p-2 text-left">Κινητό</th>
                  <th className="p-2 text-left">Email</th>
                  {isAdmin && <th className="p-2 text-center">Ενέργειες</th>}
                </tr>
              </thead>
              <tbody>
                {judges.map((j, idx) => (
                  <tr key={j.id} className="border-t hover:bg-slate-50" data-testid={`pj-row-${idx}`}>
                    <td className="p-2 text-slate-400">{idx + 1}</td>
                    <td className="p-2 font-medium">{j.full_name}</td>
                    <td className="p-2 text-xs">
                      <Badge variant="outline" className="text-[10px]">{j.grade || "—"}</Badge>
                    </td>
                    <td className="p-2 text-xs font-mono">
                      {j.mobile ? <><Phone className="w-3 h-3 inline mr-1 text-slate-400" />{j.mobile}</> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-2 text-xs">
                      {j.email ? <><Mail className="w-3 h-3 inline mr-1 text-slate-400" />{j.email}</> : <span className="text-slate-300">—</span>}
                    </td>
                    {isAdmin && (
                      <td className="p-2 text-center whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(j)} data-testid={`pj-edit-${idx}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => removeJudge(j)} data-testid={`pj-del-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Επεξεργασία Δικαστή" : "Προσθήκη Δικαστή"} — {courtType}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs">Ονοματεπώνυμο *</Label>
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="pj-form-name" />
            </div>
            <div>
              <Label className="text-xs">Βαθμός</Label>
              <Select value={form.grade} onValueChange={v => setForm({ ...form, grade: v })}>
                <SelectTrigger data-testid="pj-form-grade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Κινητό</Label>
                <Input value={form.mobile || ""} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="pj-form-mobile" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="pj-form-email" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Άκυρο</Button>
            <Button onClick={save} data-testid="pj-form-save">Αποθήκευση</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PretrialJudgesPanel;
